import React from "react";
import { Link, useNavigate } from "@remix-run/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useApi } from "@core/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@core/components/ui/dialog";

type MarketingCategoryLinkProps = {
  /** Marketing category SEO URL, e.g. "mkt-bhrt". */
  seoUrl: string;
  /** Category display name, used as the modal title. */
  label: string;
  className?: string;
  children: React.ReactNode;
  "data-testid"?: string;
};

/**
 * Links to the catalog filtered by a marketing category. Before navigating it runs the
 * same catalog search (menu and controlled-substance rules apply server-side); when the
 * customer has no products in the category, it shows the admin-configured notice instead.
 */
export function MarketingCategoryLink({
  seoUrl,
  label,
  className,
  children,
  "data-testid": testId,
}: MarketingCategoryLinkProps) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [checking, setChecking] = React.useState(false);
  const [noticeOpen, setNoticeOpen] = React.useState(false);
  const to = `/catalog?marketingCategory=${encodeURIComponent(`/${seoUrl}`)}`;

  const onClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    if (checking) {
      return;
    }

    setChecking(true);
    try {
      const hasProducts = await queryClient.fetchQuery({
        queryKey: ["marketing-category-has-products", seoUrl],
        queryFn: () =>
          api["/catalog/search"]({ marketingCategory: `/${seoUrl}`, PageIndex: 0, PageSize: 1 })
            .then((res) => (res?.TotalCount ?? 0) > 0),
        staleTime: 60_000,
      });
      if (hasProducts) {
        navigate(to);
      } else {
        setNoticeOpen(true);
      }
    } catch {
      navigate(to);
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <Link
        to={to}
        className={className}
        onClick={onClick}
        aria-busy={checking || undefined}
        style={checking ? { cursor: "progress" } : undefined}
        data-testid={testId}
      >
        {children}
      </Link>
      <EmptyCategoryNoticeDialog open={noticeOpen} onOpenChange={setNoticeOpen} title={label} />
    </>
  );
}

/** Shared empty-menu notice used by footer product links and locked featured slides. */
export function EmptyCategoryNoticeDialog({
  open,
  onOpenChange,
  title,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const { data: notice } = useQuery({
    queryKey: ["empty-category-notice"],
    queryFn: () => api.GetEmptyCategoryNotice(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const message =
    notice?.Message ||
    t(
      "ui.emptyCategoryDefaultMessage",
      "There are currently no products in this category on your menu. Contact us to learn more.",
    );
  const paragraphs = message
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const email = notice?.ContactEmail;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="empty-category-notice">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 pt-2 text-base text-white/80">
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="whitespace-pre-line">{paragraph}</p>
              ))}
            </div>
          </DialogDescription>
        </DialogHeader>
        {email && (
          <p className="text-base">
            <a
              href={`mailto:${email}`}
              className="font-semibold text-[#EFBD45] hover:underline"
              data-testid="empty-category-notice-email"
            >
              {email}
            </a>
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
